import { SITE_LEVEL_PAGE_ID } from '@wix/a11y-wizard-common';
import { Box } from '@wix/design-system';
import { useTranslation } from '@wix/fe-essentials/react';
import { CollapseList, EditorHost, RuleContent, StatusIcon, TaskListItem, useAutoExpandFailedRules } from '@wix/rules-panel-ui';
import React, { useEffect, useMemo, useState } from 'react';
import { AnalysisState, usePanelStateAtom } from '../../../../../hooks/rules-state';
import { useA11yWizardAdapter } from '../../../../../hooks/use-a11y-wizard-adapter';
import { useGetRuleIdToLabelMap } from '../../../../../hooks/use-get-ruleId-to-label-map';
import { useQueryRuleExecutionResultsQuery } from '../../../../../queries/use-query-rule-execution-results-query';
import { isStaticPage } from '../../../../../rule-mappers/shared/helpers';
import { resolveGroupAnalysisState } from '../../../../../utils/resolve-group-analysis-state';
import { useStaticPageRuleResults } from '../../rule-groups/static-page-group/use-static-page-rule-results';
import { useGetPageRuleIds } from '../utils/use-get-page-rule-ids';
import { useShowFetchErrorModal } from '../utils/use-show-fetch-error-modal';
import { CollapseActionsProvider, ItemScopeProvider, useCloseSelf } from './context/collapse-actions-context';
export const PageTaskList = /*#__PURE__*/React.memo(_ref => {
  let {
    pageId,
    isPageOpen,
    topComponent
  } = _ref;
  const showFetchErrorModal = useShowFetchErrorModal();
  const {
    refetch: refetchRuleExecutionResults
  } = useQueryRuleExecutionResultsQuery(pageId, () => showFetchErrorModal({
    cancelCallback: () => {
      closePage();
      setGroupData(pageId, {
        analysisState: AnalysisState.AnalyzeDonePendingResults
      });
    },
    tryAgainCallback: refetchRuleExecutionResults
  }));
  const closePage = useCloseSelf();
  const {
    getGroupData,
    setGroupData
  } = usePanelStateAtom();
  const groupData = getGroupData(pageId);
  if (!groupData) {
    throw new Error('Group data not found for page id: ' + pageId);
  }
  const {
    t
  } = useTranslation();
  const {
    analysisState,
    itemType
  } = groupData;
  const {
    isFetchResultSucceeded
  } = resolveGroupAnalysisState(analysisState);
  const ruleResults = useStaticPageRuleResults(pageId);
  const ruleIds = ruleResults.map(result => result.ruleId);
  const [openItems, setOpenItems] = useState(new Set());

  // Auto-fetch when group is open and results become pending
  useEffect(() => {
    if (isPageOpen && analysisState === AnalysisState.AnalyzeDonePendingResults && (isStaticPage(itemType) || pageId === SITE_LEVEL_PAGE_ID)) {
      refetchRuleExecutionResults();
    }
  }, [analysisState, isPageOpen, itemType, pageId, refetchRuleExecutionResults]);
  const {
    a11yWizardAdapter,
    editorHost
  } = useA11yWizardAdapter();
  const isEditor3 = editorHost === EditorHost.odeditor;
  const ruleIdsForSkeleton = useGetPageRuleIds(pageId, ruleIds);
  const {
    pageLevelruleIdToLabelKeyMap
  } = useGetRuleIdToLabelMap();
  const tasks = useMemo(() => {
    if (!isFetchResultSucceeded) {
      return ruleIdsForSkeleton == null ? void 0 : ruleIdsForSkeleton.map(ruleId => {
        return /*#__PURE__*/React.createElement(ItemScopeProvider, {
          id: ruleId,
          key: ruleId
        }, /*#__PURE__*/React.createElement(TaskListItem, {
          id: ruleId,
          isLoading: true,
          showBottomShadow: !isEditor3,
          showTopShadow: !isEditor3,
          title: t(pageLevelruleIdToLabelKeyMap[ruleId]),
          variant: "collapsible"
        }));
      });
    }
    return ruleResults.map((ruleResult, index, array) => {
      const isLast = index === array.length - 1;
      return /*#__PURE__*/React.createElement(ItemScopeProvider, {
        id: ruleResult.ruleId,
        key: ruleResult.ruleId
      }, /*#__PURE__*/React.createElement(TaskListItem, {
        id: ruleResult.ruleId,
        prefix: /*#__PURE__*/React.createElement(StatusIcon, {
          status: ruleResult.status
        }),
        showBottomShadow: !isLast && !isEditor3,
        showTopShadow: !isEditor3,
        title: ruleResult.label,
        variant: "collapsible",
        onToggle: () => {
          a11yWizardAdapter.moveToPage(pageId);
        }
      }, /*#__PURE__*/React.createElement(Box, {
        dataHook: "page-task-list-item-content",
        padding: "SP3 SP6 SP8 SP6"
      }, /*#__PURE__*/React.createElement(RuleContent, {
        ruleResult: ruleResult
      }))));
    });
  }, [isFetchResultSucceeded, ruleResults, ruleIdsForSkeleton, isEditor3, t, pageLevelruleIdToLabelKeyMap, a11yWizardAdapter, pageId]);
  const ruleResultsForAutoExpand = useMemo(() => {
    return ruleResults.map(result => {
      return {
        id: result.ruleId,
        status: result.status
      };
    });
  }, [ruleResults]);
  useAutoExpandFailedRules({
    items: ruleResultsForAutoExpand,
    setOpenItems
  });
  return /*#__PURE__*/React.createElement(CollapseActionsProvider, {
    setOpenItems: setOpenItems
  }, /*#__PURE__*/React.createElement(CollapseList, {
    contentGap: 0,
    openItems: openItems,
    setOpenItems: setOpenItems
  }, topComponent, tasks));
});
//# sourceMappingURL=page-task-list.js.map